import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { enqueueSnackbar } from "notistack";

import { ListAlt } from "@mui/icons-material";
import { Box } from "@mui/material";

import { TrainArrangementSortColumn } from "@framework/types";
import { ProgressOverlay } from "@framework/mui-page-layout";
import { useApiCall } from "@framework/react-hooks";

import { EmptyPlaceholder } from "../../../../../../components/common/empty-placeholder";
import { DataExportButton } from "../../../../../../components/common/buttons/export-button";
import { TrainArrangementsTable } from "./table";
import { downloadCsv } from "../../../../../../utils/download-csv";
import { useFormatDateHumanReadable, useToFakeUtcISO } from "../../../../../../hooks";
import { useTrainTables } from "../hooks/use-train-tables";
import { ITrainArrangementResponse, ITrainArrangementRequest, ITrainArrangementExportResponse } from "./interfaces";

export const TrainArrangements: FC<{ isActiveTableTab?: boolean }> = props => {
  const { isActiveTableTab } = props;

  const { formatMessage } = useIntl();
  const formatDate = useFormatDateHumanReadable();
  const toFakeUtcISO = useToFakeUtcISO();

  const [trainArrangements, setTrainArrangements] = useState<ITrainArrangementResponse[]>([]);
  const [isTrainArrangementActionsLoading, setIsTrainArrangementActionsLoading] = useState<boolean>(false);

  const {
    trainId,
    startTimestamp,
    endTimestamp,
    isRecorderMode,
    recorderWindowStart,
    sortColumn,
    sortOrder,
    handleSort,
    canFetch,
  } = useTrainTables({
    isActiveTableTab,
    defaultSortColumn: TrainArrangementSortColumn.StartDate,
    onReset: () => setTrainArrangements([]),
  });

  const noTrainArrangements = formatMessage({ id: "pages.scheme.trainArrangements.empty" });

  const { fn: getTrainArrangements, isLoading: isTrainArrangementsLoading } = useApiCall(
    (api, data: ITrainArrangementRequest) => {
      return api
        .fetchJson({
          url: "/train-arrangement",
          method: "GET",
          data,
        })
        .then((json: ITrainArrangementResponse[] | null) => json ?? []);
    },
    { success: false },
  );

  const { fn: exportTrainArrangements, isLoading: isExportTrainArrangementsLoading } = useApiCall(
    (api, data: ITrainArrangementRequest) => {
      return api
        .fetchJson({
          url: "/train-arrangement/export",
          method: "GET",
          data,
        })
        .then((json: ITrainArrangementExportResponse[] | null) => {
          if (json === null) {
            return [];
          }
          return json;
        });
    },
    { success: false },
  );

  const applyFakeUtcToArrangements = (items: ITrainArrangementResponse[]): ITrainArrangementResponse[] =>
    items.map(item => ({
      ...item,
      StartDate: toFakeUtcISO(item.StartDate),
      EndDate: toFakeUtcISO(item.EndDate),
    }));

  const handleExport = async (): Promise<void> => {
    try {
      if (!trainArrangements.length || !trainId) {
        return;
      }

      const exportedData = await exportTrainArrangements(void 0, {
        idTrain: trainId,
        startTimestamp,
        endTimestamp,
        sortColumn,
        sortOrder,
      });

      if (!exportedData?.length) {
        return;
      }

      const result = exportedData.map(trainArrangement => {
        const {
          idTrainArrangement,
          StartDate,
          EndDate,
          Type,
          Description,
          ActionDescription,
          ActionEndDate,
          ActionName,
          ActionObject,
          ActionStartDate,
        } = trainArrangement;

        return {
          id: idTrainArrangement,
          name: formatMessage({ id: `enums.trainArrangementType.${Type}` }),
          startDate: formatDate(StartDate),
          endDate: formatDate(EndDate),
          description: Description,
          actionName: ActionName,
          actionDescription: ActionDescription,
          actionObject: ActionObject || formatMessage({ id: "pages.scheme.trainArrangements.noValue" }),
          actionStartDate: formatDate(ActionStartDate),
          actionEndDate: formatDate(ActionEndDate),
        };
      });

      const date = toFakeUtcISO(new Date()).split("T")[0];
      const name = `train-arrangements-report-${trainId}-${date}.csv`;
      downloadCsv(result, name, [
        { field: "id", title: "Id" },
        { field: "name", title: formatMessage({ id: "pages.scheme.trainArrangements.headers.name" }) },
        { field: "startDate", title: formatMessage({ id: "pages.scheme.trainArrangements.headers.startDate" }) },
        { field: "endDate", title: formatMessage({ id: "pages.scheme.trainArrangements.headers.endDate" }) },
        {
          field: "description",
          title: formatMessage({ id: "pages.scheme.trainArrangements.headers.description" }),
        },
        {
          field: "actionName",
          title: formatMessage({ id: "pages.scheme.trainArrangements.headers.actionName" }),
        },
        {
          field: "actionDescription",
          title: formatMessage({ id: "pages.scheme.trainArrangements.headers.actionDescription" }),
        },
        {
          field: "actionObject",
          title: formatMessage({ id: "pages.scheme.trainArrangements.headers.actionObject" }),
        },
        {
          field: "actionStartDate",
          title: formatMessage({ id: "pages.scheme.trainArrangements.headers.actionStartDate" }),
        },
        {
          field: "actionEndDate",
          title: formatMessage({ id: "pages.scheme.trainArrangements.headers.actionEndDate" }),
        },
      ]);
    } catch (error: unknown) {
      console.error("Export error:", error);
      enqueueSnackbar(formatMessage({ id: "snackbar.exportError" }), {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    if (!canFetch) return;

    void getTrainArrangements(void 0, {
      idTrain: trainId,
      startTimestamp,
      endTimestamp,
      ...(isRecorderMode ? {} : { sortColumn, sortOrder }),
    }).then(json => {
      if (!Array.isArray(json)) return;

      if (isRecorderMode) {
        setTrainArrangements(prev => {
          const converted = applyFakeUtcToArrangements(json);
          const newItems = converted.filter(
            item => !prev.some(prevItem => prevItem.idTrainArrangement === item.idTrainArrangement),
          );
          const updated = [...prev, ...newItems];
          const filtered = updated.filter(item => new Date(item.StartDate) >= new Date(recorderWindowStart));
          return filtered;
        });
      } else {
        setTrainArrangements(applyFakeUtcToArrangements(json));
      }
    });
  }, [
    trainId,
    startTimestamp,
    endTimestamp,
    sortColumn,
    sortOrder,
    isRecorderMode,
    recorderWindowStart,
    isActiveTableTab,
  ]);

  const isLoading = isTrainArrangementsLoading || isTrainArrangementActionsLoading;

  return (
    <ProgressOverlay
      isLoading={isLoading || isExportTrainArrangementsLoading}
      wrapperSx={{ position: "initial", height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}
    >
      <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
        {!trainArrangements.length ? (
          <EmptyPlaceholder icon={<ListAlt />} text={noTrainArrangements} />
        ) : (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", height: "34px" }}>
              <DataExportButton handleExport={() => void handleExport()} />
            </Box>
            <TrainArrangementsTable
              setIsLoading={setIsTrainArrangementActionsLoading}
              trainArrangements={trainArrangements}
              handleSort={handleSort}
              sortColumn={sortColumn}
              sortOrder={sortOrder}
            />
          </>
        )}
      </Box>
    </ProgressOverlay>
  );
};
